import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  SubActivitiesLabelType,
  SUB_ACTIVITIES_LABEL_TYPE_ALPHABETICAL,
  SUB_ACTIVITIES_LABEL_TYPE_NONE,
  SUB_ACTIVITIES_LABEL_TYPE_NUMERICAL,
} from 'global/constants';
import { SettingsGroup } from 'approot/activity-builder/settings/settings.style';
import { PStyled } from 'approot/shared/styles/typography.style';
import {
  CheckableSingleGroup,
  CheckableSinglePill,
} from 'approot/shared/styles/buttons/buttons';
import { ComponentSubActivity } from '../component.constants';
import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { ListButton } from 'approot/shared/styles/buttons/list-button';
import {
  ActivityProperties,
  QuestionSettingCurrentSubActivity,
  SettingsProps,
} from './activity.constants';
import {
  formatSubActivityLabel,
  getSubActivityFullLabel,
  sortSubActivities,
} from '../sub-activity/sub-activity.utils';
import { ActivitySettingsLabel } from './activity-settings.style';
import { updateSubActivityLabelInCache } from './activity-settings.utils';
import { MultiViewProps } from 'approot/shared/layout/layout-multi-view';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import { MEDIA_SM, useMediaQuery } from 'approot/shared/style';
import { Box } from 'approot/shared/design-system/box/box.style';

const ActivitySettings = ({
  id,
  sectionId,
  properties,
  subActivities,
  onSubActivitySelect,
  goForward,
}: SettingsProps &
  MultiViewProps & {
    onSubActivitySelect: (params: QuestionSettingCurrentSubActivity) => void;
  }) => {
  const client = useApolloClient();
  const { closeDrawer } = useActivityBuilderContext();

  const sortedSubActivities = sortSubActivities(subActivities || []);

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const isMediaUpSmall = useMediaQuery(MEDIA_SM);

  const handleOnChange = useCallback(
    (newProperties: Partial<ActivityProperties>) => {
      const updatedProperties = {
        ...properties,
        ...newProperties,
      };
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: updatedProperties,
          },
        },
        properties: updatedProperties,
        typename: 'ActivitiesActivityComponent',
      });
    },
    [id, properties, sectionId, updateComponentProperties]
  );

  const handleSubActivityLabelChange = useCallback(
    (type: SubActivitiesLabelType) => {
      sortedSubActivities?.forEach((subActivity, index) =>
        updateSubActivityLabelInCache(client, type, index, subActivity)
      );
      handleOnChange({ subActivitiesLabelType: type });
    },
    [client, sortedSubActivities, handleOnChange]
  );

  return (
    <ActivityBuilderDrawerWrapper>
      <ActivityBuilderDrawerHeader title="Activity" onClose={closeDrawer} />
      <ActivityBuilderDrawerContent>
        {subActivities && subActivities.length > 0 && (
          <>
            <SettingsGroup>
              <PStyled>Sub-activities</PStyled>
              <Box $marginBottom={isMediaUpSmall ? 24 : 16}>
                <CheckableSingleGroup data-selector="checkable-pill-group">
                  <CheckableSinglePill
                    data-selector="label-none"
                    data-selection="single-pill-options"
                    $isSmall
                    $isSelected={
                      properties.subActivitiesLabelType ===
                      SUB_ACTIVITIES_LABEL_TYPE_NONE
                    }
                    onClick={() =>
                      handleSubActivityLabelChange(
                        SUB_ACTIVITIES_LABEL_TYPE_NONE
                      )
                    }
                  >
                    None
                  </CheckableSinglePill>
                  <CheckableSinglePill
                    data-selector="label-numerical"
                    $isSmall
                    $isSelected={
                      properties.subActivitiesLabelType ===
                      SUB_ACTIVITIES_LABEL_TYPE_NUMERICAL
                    }
                    onClick={() =>
                      handleSubActivityLabelChange(
                        SUB_ACTIVITIES_LABEL_TYPE_NUMERICAL
                      )
                    }
                  >
                    1, 2, 3
                  </CheckableSinglePill>
                  <CheckableSinglePill
                    data-selector="label-alphabetical"
                    $isSmall
                    $isSelected={
                      properties.subActivitiesLabelType ===
                      SUB_ACTIVITIES_LABEL_TYPE_ALPHABETICAL
                    }
                    onClick={() =>
                      handleSubActivityLabelChange(
                        SUB_ACTIVITIES_LABEL_TYPE_ALPHABETICAL
                      )
                    }
                  >
                    a, b, c
                  </CheckableSinglePill>
                </CheckableSingleGroup>
              </Box>
              {sortedSubActivities.map((sa: ComponentSubActivity) => (
                <ListButton
                  data-selector="sub-activity-list"
                  key={sa.id}
                  onClick={() => {
                    onSubActivitySelect(sa);
                    goForward();
                  }}
                >
                  <ActivitySettingsLabel data-selector="sub-activity-title">
                    <strong>
                      {formatSubActivityLabel(
                        sa.subActivityProperties?.subActivityLabel
                      )}
                    </strong>
                    {getSubActivityFullLabel(sa.type)}
                  </ActivitySettingsLabel>
                </ListButton>
              ))}
            </SettingsGroup>
          </>
        )}
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default ActivitySettings;
